import { formatQuotedPrice } from "@shared-core/domain/formatters";
import {
  DELIVERY_NOT_YET_PRICED,
  NOT_YET_PRICED,
} from "@shared-core/domain/checkout-delivery";

// The price breakdown, the same on the quote and the order page (Ben,
// 5 Oct): a row per line (ref, quantity, title, price ex VAT, and its VAT
// rate where known), then the total + VAT, and the total including VAT
// when it is known. A quote line still to choose shows "choose" and the
// total waits for it. A delivery not yet priced reads "Not yet priced",
// and the total, which leaves it out, says so.

export interface PriceRow {
  ref: string;
  title: string;
  quantity: number | null;
  /** Null: a quantity is still to choose, or (with `is_not_yet_priced`) a delivery not yet priced. */
  price_pence: number | null;
  vat_label?: string | null;
  /** A delivery with no price yet: it reads "Not yet priced" and is in no total. */
  is_not_yet_priced?: boolean;
}

/** A line staff cancelled: no price, and in no total. */
export interface CancelledPriceRow {
  ref: string;
  title: string;
  /** "Cancelled on Wed 7 Oct", the read's words. */
  status: string;
}

export function PriceBreakdown({
  rows,
  cancelledRows = [],
  totalPence,
  totalIncVatPence,
  missingHint,
}: {
  rows: PriceRow[];
  /** Cancelled lines, greyed, after the live ones. */
  cancelledRows?: CancelledPriceRow[];
  /** Null while a choice is still to make. */
  totalPence: number | null;
  totalIncVatPence?: number | null;
  missingHint?: string | null;
}) {
  return (
    <div className="space-y-3">
      <ul className="divide-y text-sm">
        {rows.map((r) => (
          <li
            key={r.ref}
            className="flex items-start justify-between gap-3 py-2 first:pt-0"
          >
            <span className="min-w-0">
              <span className="text-muted-foreground tabular-nums">
                {r.ref}
              </span>{" "}
              {r.quantity !== null
                ? `${r.quantity.toLocaleString("en-GB")} × `
                : ""}
              {r.title}
            </span>
            <span className="shrink-0 text-right tabular-nums">
              {r.price_pence !== null ? (
                formatQuotedPrice(r.price_pence)
              ) : r.is_not_yet_priced ? (
                <span className="text-muted-foreground">{NOT_YET_PRICED}</span>
              ) : (
                <span className="text-muted-foreground">choose</span>
              )}
              {!r.is_not_yet_priced && r.vat_label && (
                <span className="block text-xs text-muted-foreground">
                  {r.vat_label}
                </span>
              )}
            </span>
          </li>
        ))}
        {cancelledRows.map((r) => (
          <li
            key={`cancelled-${r.ref}`}
            className="flex items-start justify-between gap-3 py-2 text-muted-foreground"
          >
            <span className="min-w-0">
              <span className="tabular-nums">{r.ref}</span> {r.title}
            </span>
            <span className="shrink-0 text-right text-xs">{r.status}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-baseline justify-between border-t pt-3 text-sm">
        <span className="font-medium">Total</span>
        <span className="text-right tabular-nums">
          <span className="font-semibold">
            {totalPence !== null ? formatQuotedPrice(totalPence) : "—"}
          </span>{" "}
          + VAT
          {totalIncVatPence != null && (
            <span className="block text-muted-foreground">
              {formatQuotedPrice(totalIncVatPence)} including VAT
            </span>
          )}
          {rows.some((r) => r.is_not_yet_priced) && (
            <span className="block text-muted-foreground">
              {DELIVERY_NOT_YET_PRICED}
            </span>
          )}
        </span>
      </div>
      {missingHint && (
        <p className="text-xs text-muted-foreground">{missingHint}</p>
      )}
    </div>
  );
}
